import type React from "react";
import { useState } from "react";
import { Box, Button, CircularProgress, Paper, useTheme } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import ReusableStepper from "../../components/common/Stepper";
import { StepItem } from "../../types/steps";
import VenueDetails from "./steps/VenueDetails";
import { tokens } from "../../theme";
import InternetDetails from "./steps/InternetDetails";
import VenuePhotos from "./steps/AddVenuePhotos";
import VenueConfirmation from "./steps/AddVenueConfirmation";
import apiService from "../../service/Api";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logout } from "../../store/slice/AuthSlice";
import { VenueFormData } from "../../types/venue";
import {
  showErrorToast,
  showSuccessToast,
} from "../../components/common/ShowToast";
import AddVenuePhotos2 from "./steps/AddVenuePhotos2";

interface VenueResponse {
  id: number;
}

const AddVenue: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<VenueFormData>({
    venueName: "",
    venueType: "",
    addressOne: "",
    addressTwo: "",
    suburb: "",
    city: "Harare",
    capacity: "",
    contacts_ids: [],
    layouts: [{ layoutType: "", capacity: "" }],
    contactPersonName: "",
    contactPersonSurname: "",
    contactPersonEmail: "",
    contactPersonPhone: "",
    doesVenueHavePower: "yes",
    powerType: "",
    powerOutageRate: "",
    powerBackup: "",
    backupPowerCapacity: "",
    router_distance_from_livestream: "",
    powerSocketType: "",
    internet_service_provider: "",
    wifi_name: "",
    wifi_password: "",
    internet_upload_speed: "",
    power_distance_from_livestream_desk: "",
    pa_system_distance_from_livestream: "",
    router_accessibility: "yes",
    has_pa_system: "yes",
    pa_system_provider: "",
    pa_system_contact_name: "",
    other_pa_system_service_provider: "",
    pa_system_contact_phone: "",
    pa_system_contact_email: "",
    uploadedFiles: {
      venue: [],
      paSystem: [],
      equipment: [],
      floorPlan: [],
    },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const steps: StepItem[] = [
    { label: "Venue & Power Details" },
    { label: "Internet & PA System" },
    { label: "Venue Photos" },
    { label: "Confirmation" },
  ];

  const handleInputChange = (field: keyof VenueFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLayoutChange = (
    index: number,
    field: "layoutType" | "capacity",
    value: string
  ) => {
    const updatedLayouts = [...formData.layouts];
    updatedLayouts[index] = { ...updatedLayouts[index], [field]: value };
    setFormData((prev) => ({
      ...prev,
      layouts: updatedLayouts,
    }));
  };

  const addLayout = () => {
    setFormData((prev) => ({
      ...prev,
      layouts: [...prev.layouts, { layoutType: "", capacity: "" }],
    }));
  };

  const handleFilesChange =
    (category: keyof VenueFormData["uploadedFiles"]) => (files: File[]) => {
      // Filter to ensure only valid File objects
      const validFiles = files.filter(
        (file) => file instanceof File && file.size > 0 && file.name
      );
      setFormData((prev) => ({
        ...prev,
        uploadedFiles: {
          ...prev.uploadedFiles,
          [category]: validFiles,
        },
      }));
    };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const formDataToSend = new FormData();

      // Filter out layouts with empty layoutType to avoid validation errors
      const validLayouts = formData.layouts.filter(
        (layout) => layout.layoutType.trim() !== ""
      );

      const payload = {
        name: formData.venueName,
        venue_type: formData.venueType,
        address_one: formData.addressOne,
        address_two: formData.addressTwo,
        suburb: formData.suburb,
        city: formData.city,
        capacity: formData.capacity,
        layouts: validLayouts, // Use filtered layouts
        contact_person_name: formData.contactPersonName,
        contact_id: formData.contact_id,
        contacts_ids: [
          ...(Array.isArray(formData.contacts_ids)
            ? formData.contacts_ids
            : []),
          ...(formData.contact_id ? [Number(formData.contact_id)] : []),
        ],
        contact_person_surname: formData.contactPersonSurname,
        contact_person_email: formData.contactPersonEmail,
        contact_person_phone: formData.contactPersonPhone,
        power_distance_from_livestream_desk:
          formData.power_distance_from_livestream_desk
            ? parseInt(formData.power_distance_from_livestream_desk)
            : null,
        does_venue_have_power: formData.doesVenueHavePower,
        power_type: formData.powerType,
        power_outage_rate: formData.powerOutageRate,
        power_backup: formData.powerBackup,
        backup_power_capacity: formData.backupPowerCapacity,
        router_distance_from_livestream:
          formData.router_distance_from_livestream
            ? parseInt(formData.router_distance_from_livestream)
            : null,
        power_socket_type: formData.powerSocketType,
        internet_service_provider: formData.internet_service_provider,
        wifi_name: formData.wifi_name,
        wifi_password: formData.wifi_password,
        internet_upload_speed: formData.internet_upload_speed,
        router_accessibility: formData.router_accessibility,
        has_pa_system: formData.has_pa_system,
        pa_system_provider: formData.pa_system_provider,
        pa_system_distance_from_livestream:
          formData.pa_system_distance_from_livestream
            ? parseInt(formData.pa_system_distance_from_livestream)
            : null,
        pa_system_contact_name: formData.pa_system_contact_name,
        other_pa_system_service_provider:
          formData.other_pa_system_service_provider,
        pa_system_contact_phone: formData.pa_system_contact_phone,
        pa_system_contact_email: formData.pa_system_contact_email,
      };

      formDataToSend.append("data", JSON.stringify(payload));

      // Append files to FormData with validation
      let hasFiles = false;
      if (formData.uploadedFiles.venue.length > 0) {
        formData.uploadedFiles.venue.forEach((file: File, index: number) => {
          if (
            file instanceof File &&
            file.size > 0 &&
            file.name &&
            file.type.startsWith("image/")
          ) {
            formDataToSend.append(`venue_photos[${index}]`, file);
            hasFiles = true;
          } else {
            console.warn(
              `Skipping invalid venue file at index ${index}:`,
              file
            );
          }
        });
      }

      if (formData.uploadedFiles.paSystem.length > 0) {
        formData.uploadedFiles.paSystem.forEach((file: File, index: number) => {
          if (
            file instanceof File &&
            file.size > 0 &&
            file.name &&
            file.type.startsWith("image/")
          ) {
            formDataToSend.append(`pa_system_photos[${index}]`, file);
            hasFiles = true;
          } else {
            console.warn(
              `Skipping invalid paSystem file at index ${index}:`,
              file
            );
          }
        });
      }

      if (formData.uploadedFiles.equipment.length > 0) {
        formData.uploadedFiles.equipment.forEach(
          (file: File, index: number) => {
            if (
              file instanceof File &&
              file.size > 0 &&
              file.name &&
              file.type.startsWith("image/")
            ) {
              formDataToSend.append(`equipment_photos[${index}]`, file);
              hasFiles = true;
            } else {
              console.warn(
                `Skipping invalid equipment file at index ${index}:`,
                file
              );
            }
          }
        );
      }

      if (formData.uploadedFiles.floorPlan.length > 0) {
        formData.uploadedFiles.floorPlan.forEach(
          (file: File, index: number) => {
            if (
              file instanceof File &&
              file.size > 0 &&
              file.name &&
              (file.type.startsWith("image/") ||
                file.type === "application/pdf")
            ) {
              formDataToSend.append(`floor_plan_photos[${index}]`, file);
              hasFiles = true;
            } else {
              console.warn(
                `Skipping invalid floorPlan file at index ${index}:`,
                file
              );
            }
          }
        );
      }

      for (const [key, value] of Array.from(formDataToSend.entries())) {
      }

      // Check if there are any files to send
      if (!hasFiles) {
        console.warn(
          "No valid files to submit. Sending payload without files."
        );
      }

      // Send API request using postFile
      const response = await apiService.post(
        "/api/venues/venue/full-create/",
        formDataToSend,
        false // skipAuth: false to include Authorization header
      );

      const responseData = response.data as VenueResponse;

      showSuccessToast("Venue submitted successfully!");
      setTimeout(() => {
        navigate(`/venues/view/${responseData.id}`);
      }, 2000);
    } catch (error: any) {
      let errorMessage = "Failed to submit venue. Please try again.";
      if (error.response?.status === 401) {
        errorMessage = "Unauthorized: Please log in again.";
        dispatch(logout());
        navigate("/login");
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
        if (error.response.data.errors) {
          // Format specific errors
          const fileErrors = error.response.data.errors;
          const errorDetails = Object.entries(fileErrors)
            .map(([field, errors]) => {
              if (Array.isArray(errors)) {
                // Handle array of strings (e.g., layout_type errors)
                return `${field}: ${errors.join(", ")}`;
              } else if (typeof errors === "object" && errors !== null) {
                // Handle nested object errors (e.g., file upload errors)
                return `${field}: ${Object.entries(errors)
                  .map(([index, messages]) => {
                    if (Array.isArray(messages)) {
                      return `File ${index}: ${messages.join(", ")}`;
                    } else if (typeof messages === "string") {
                      return `File ${index}: ${messages}`;
                    } else {
                      return `File ${index}: ${JSON.stringify(messages)}`;
                    }
                  })
                  .join("; ")}`;
              }
              return `${field}: ${JSON.stringify(errors)}`;
            })
            .join("; ");
          errorMessage += `: ${errorDetails}`;
        }
      } else if (error.response?.data) {
        errorMessage = JSON.stringify(error.response.data);
      }
      showErrorToast(errorMessage);
      console.error("Error submitting venue:", error, {
        responseData: error.response?.data,
        status: error.response?.status,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <VenueDetails
              setFormData={setFormData}
              formData={formData}
              handleInputChange={(field, value) =>
                handleInputChange(field as keyof VenueFormData, value)
              }
              handleLayoutChange={(index, field, value) =>
                handleLayoutChange(
                  index,
                  field as "layoutType" | "capacity",
                  value
                )
              }
              addLayout={addLayout}
            />
          </Box>
        );
      case 1:
        return (
          <Box>
            <InternetDetails
              formData={formData}
              handleInputChange={(field, value) =>
                handleInputChange(field as keyof VenueFormData, value)
              }
              handleLayoutChange={(index, field, value) =>
                handleLayoutChange(
                  index,
                  field as "layoutType" | "capacity",
                  value
                )
              }
              setFormData={setFormData}
              addLayout={addLayout}
            />
          </Box>
        );
      case 2:
        return (
          <Box>
            <AddVenuePhotos2
              uploadedFiles={formData.uploadedFiles}
              handleFilesChange={handleFilesChange}
            />
          </Box>
        );
      case 3:
        return (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <VenueConfirmation
              formData={formData}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 4, maxWidth: 1200, mx: "auto" }}>
      <ReusableStepper
        steps={steps.map((step) => step.label)}
        activeStep={activeStep}
        onStepClick={setActiveStep}
      />

      <Box sx={{ minHeight: 400 }}>{renderStepContent()}</Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
        <Button
          startIcon={<KeyboardArrowLeft />}
          onClick={handlePrevious}
          disabled={activeStep === 0 || isSubmitting}
          sx={{
            color: colors.primary[900],
            backgroundColor: colors.primary[400],
            "&.Mui-disabled": {
              backgroundColor: colors.primary[800],
              color: colors.grey[600],
            },
          }}
        >
          Previous
        </Button>

        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              color: colors.primary[900],
              backgroundColor: colors.primary[400],
            }}
            startIcon={
              isSubmitting && <CircularProgress size={20} color="inherit" />
            }
          >
            Submit
          </Button>
        ) : (
          <Button
            variant="contained"
            endIcon={<KeyboardArrowRight />}
            onClick={handleNext}
            disabled={isSubmitting}
            sx={{
              color: colors.primary[900],
              backgroundColor: colors.primary[400],
            }}
          >
            Next
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default AddVenue;
